/**
 * `/lists` — the list of lists, with the writes the API has always had.
 *
 * The screen shipped read-only under a placard that named the three procedures
 * it was not calling. All three exist and are wired here: `lists.create`,
 * `lists.update` (rename) and `lists.delete`. `shares.list.create` was already
 * wired one level down, on the list detail page.
 *
 * Search IS server-side: `lists.list` honours `q` (it is `contacts.list.q` that
 * 422s), so the box in the header is a real filter, debounced through the URL so
 * a filtered view is linkable.
 */

import { Badge, Button, EmptyState, Input, SkeletonRows } from '@crm/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ListIcon, PencilIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { useDeleteListMutation, useListsQuery } from '../../../api/queries.ts';
import { ConfirmDialog } from '../../../components/confirm-dialog.tsx';
import { ListFormDialog } from '../../../components/lists/list-form-dialog.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { parseListsSearch } from '../../../lib/search.ts';

export const Route = createFileRoute('/_authed/lists/')({
  validateSearch: parseListsSearch,
  component: ListsPage,
  errorComponent: RouteErrorPanel,
});

type ListRow = {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly visibility: 'org' | 'private';
  readonly kind: string;
  readonly itemCount: number;
  readonly shareCount: number;
};

function ListsPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate();
  const lists = useListsQuery(q ?? '');
  const remove = useDeleteListMutation();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ListRow | null>(null);
  const [deleting, setDeleting] = useState<ListRow | null>(null);

  const items = (lists.data?.items ?? []) as readonly ListRow[];
  const searching = (q ?? '') !== '';

  return (
    <>
      <PageHeader
        title="Lists"
        description="Static and smart groupings of contacts."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            New list
          </Button>
        }
      />

      <div className="border-b border-border px-4 py-3">
        <div className="relative max-w-sm">
          <SearchIcon
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            defaultValue={q ?? ''}
            aria-label="Search lists"
            placeholder="Search lists"
            className="pl-8"
            onChange={(event) => {
              const value = event.target.value.trim();
              void navigate({
                to: '/lists',
                search: value === '' ? {} : { q: value },
                replace: true,
              });
            }}
          />
        </div>
      </div>

      <PageBody>
        {lists.isLoading ? (
          <SkeletonRows rows={5} />
        ) : lists.isError ? (
          <EmptyState kind="error" title="Could not load lists" />
        ) : items.length === 0 && searching ? (
          <EmptyState
            kind="filtered"
            title="No lists match that"
            description="Your lists are still there — this search just excludes all of them."
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={() => void navigate({ to: '/lists', search: {} })}
              >
                Clear search
              </Button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ListIcon aria-hidden />}
            title="No lists yet"
            description="Lists are how you group people you want to treat the same way — a fund's portfolio, a dinner's guests, a hiring pipeline."
            action={
              <Button size="sm" onClick={() => setCreating(true)}>
                Create your first list
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {items.map((list) => (
              <li key={list.id} className="flex items-stretch">
                {/* The row is a link and the two controls are buttons BESIDE it,
                    never nested inside: a button inside an anchor is invalid HTML
                    and, on a touch device, is a coin-flip about which one fired. */}
                <Link
                  to="/lists/$listId"
                  params={{ listId: list.id }}
                  className="flex min-h-11 min-w-0 flex-1 flex-col justify-center gap-0.5 px-4 py-2.5 text-sm hover:bg-accent/50 sm:flex-row sm:items-center sm:gap-2"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-medium">{list.name}</span>
                    <Badge variant="muted">{list.kind}</Badge>
                    {list.shareCount > 0 ? (
                      <Badge variant="secondary">shared ×{list.shareCount}</Badge>
                    ) : null}
                    {list.visibility === 'private' ? (
                      <Badge variant="outline">only me</Badge>
                    ) : null}
                  </span>
                  <span className="text-xs text-muted-foreground sm:ml-auto">
                    {list.itemCount} contacts
                  </span>
                </Link>
                <div className="flex shrink-0 items-center gap-0.5 pr-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Rename ${list.name}`}
                    onClick={() => setEditing(list)}
                  >
                    <PencilIcon aria-hidden />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-destructive"
                    aria-label={`Delete ${list.name}`}
                    onClick={() => setDeleting(list)}
                  >
                    <Trash2Icon aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <ListFormDialog
          open={creating}
          onOpenChange={setCreating}
          onSaved={(created) =>
            void navigate({ to: '/lists/$listId', params: { listId: created.id } })
          }
        />
        {editing === null ? null : (
          <ListFormDialog
            open
            list={editing}
            onOpenChange={(open) => {
              if (!open) setEditing(null);
            }}
          />
        )}
        {deleting === null ? null : (
          <ConfirmDialog
            open
            onOpenChange={(open) => {
              if (!open) setDeleting(null);
            }}
            title={`Delete “${deleting.name}”?`}
            description={`The ${deleting.itemCount} contact${
              deleting.itemCount === 1 ? '' : 's'
            } on it stay in your CRM — only the grouping goes. Anyone this list was shared with loses access to the members it exposed.`}
            confirmLabel="Delete list"
            onConfirm={() => remove.mutateAsync(deleting.id)}
          />
        )}
      </PageBody>
    </>
  );
}
